<template>
  <div
    v-if="shouldRenderBlock"
    :id="uuid"
    :style="{
      'scroll-margin-top': pageEditorStore.scrollMarginTop,
    }"
    class="relative"
  >
    <PaymentCloudPayments
      ref="cloudPaymentsComponent"
      @done="onPaymentDone"
      @close="onPaymentClose"
    />

    <BaseImage
      v-if="formData.bgImage && formData.useImageRatio"
      :src="formData.bgImage"
      loading="lazy"
      class="w-full h-auto overflow-hidden rounded-xl"
    />

    <div
      v-if="adminHiddenMessage"
      class="absolute top-4 left-1/2 -translate-x-1/2 z-20 max-w-[calc(100%-2rem)] bg-white text-red-700 text-center px-4 py-2 rounded-lg font-semibold whitespace-pre-line"
    >
      {{ adminHiddenMessage }}
    </div>

    <div
      class="flex px-6 md:px-8 overflow-hidden rounded-xl"
      :class="[
        flexAlignClass,
        formData.bgImage && formData.useImageRatio
          ? 'items-center absolute top-0 left-0 w-full h-full'
          : paddingClass,
      ]"
      :style="{
        background: !formData.bgImage
          ? formData.bgColor || '#000000'
          : undefined,
        color: formData.textColor,
        '--text-color': formData.textColor,
        ...useEditorFontFamily(formData.textFontFamily),
      }"
    >
      <BaseImage
        v-if="formData.bgImage"
        :src="formData.bgImage"
        loading="lazy"
        class="absolute inset-0 w-full h-full object-cover rounded-xl"
      />

      <div
        class="flex flex-col gap-4 md:gap-8 w-full relative"
        :style="{
          maxWidth: formData.maxWidth ? `${formData.maxWidth}px` : '576px',
        }"
      >
        <BaseImage
          v-if="formData.logo"
          :src="formData.logo"
          loading="lazy"
          class="w-full h-auto"
          :style="{
            maxWidth: (formData.logoWidth || 240) + 'px',
          }"
        />

        <div
          class="marked"
          :class="[`text-${formData.textAlign}`]"
          v-html="
            getMarkedTextWithBlankLinks(formData.text, formData.linkColor)
          "
        />

        <div
          v-if="isButtonsVisible"
          class="flex flex-wrap gap-4 items-center"
          :class="flexAlignClass"
        >
          <template v-if="isAdditionalButtonsOnTheLeft">
            <template
              v-for="(button, index) in formData.additionalButtons || []"
              :key="`left-${index}`"
            >
              <EditorButton :button="button" />
            </template>
          </template>

          <button
            v-if="hasSubscriptionButtonText"
            type="button"
            :disabled="isSubscriptionButtonDisabled"
            class="relative inline-flex items-center justify-center focus:outline-none transition duration-150 ease-in-out select-none"
            :class="[
              subscriptionButtonClass,
              formData.subscriptionButton?.type === 'button'
                ? 'h-10 px-5 text-base font-medium rounded-xl'
                : 'px-5',
              isSubscriptionButtonDisabled && 'opacity-70 pointer-events-none',
            ]"
            :style="{
              ...useEditorFontFamily(
                formData.subscriptionButton?.textFontFamily
              ),
            }"
            @click="submit"
          >
            <span
              class="inline-flex items-center truncate"
              :class="isPaymentDataLoading && 'opacity-0'"
            >
              {{ formData.subscriptionButton?.text }}
            </span>
            <span
              v-if="isPaymentDataLoading"
              class="absolute inset-0 flex items-center justify-center"
            >
              <BaseSpinner />
            </span>
          </button>

          <template v-if="!isAdditionalButtonsOnTheLeft">
            <template
              v-for="(button, index) in formData.additionalButtons || []"
              :key="`right-${index}`"
            >
              <EditorButton :button="button" />
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { FetchError } from 'ofetch'
import type { ButtonInterface, ButtonStyleInterface } from '../common'
import { useSubscriptionPurchase } from '../subscription/use-subscription-purchase'
import type { Plan } from '~/models/subscriptions'
import PaymentCloudPayments from '~/components/payment/PaymentCloudPayments.vue'
import EditorButton from '~/components/page-editor/EditorButton.vue'
import { usePageEditor } from '~/stores/page-editor'
import { useSubscriptions } from '~/stores/subscriptions'
import { useAuth } from '~/stores/auth'
import {
  DEFAULT_COLOR_PRIMARY,
  DEFAULT_COLOR_PRIMARY_HOVER,
  DEFAULT_COLOR_WHITE,
} from '~/components/page-editor/constants'

interface FormDataInterface {
  planId?: number
  text: string
  textColor?: string
  linkColor?: string
  textFontFamily?: string
  textAlign?: 'left' | 'center' | 'right'
  paddingSize: 'xs' | 'sm' | 'md' | 'lg'
  useImageRatio: boolean
  bgImage?: string
  bgColor?: string
  additionalButtons?: ButtonInterface[]
  additionalButtonsPosition?: 'left' | 'right'
  subscriptionButton?: Partial<ButtonStyleInterface>
  maxWidth?: number
  logo?: string
  logoWidth?: number
}

interface PropsInterface {
  uuid: string
  formData: FormDataInterface
}

const props = defineProps<PropsInterface>()

const authStore = useAuth()
const pageEditorStore = usePageEditor()
const subscriptionsStore = useSubscriptions()
const { getMarkedTextWithBlankLinks } = useMarkedText()
const {
  cloudPaymentsComponent,
  isPaymentDataLoading,
  onPaymentClose,
  onPaymentDone,
  submitPlan,
} = useSubscriptionPurchase()

const buildStateKey = (suffix: string) =>
  `subscription-cover:${props.uuid}:${suffix}`

const plan = useState<Plan | null>(buildStateKey('plan'), () => null)
const isResolved = useState(buildStateKey('is-resolved'), () => false)
const isPlanNotFound = useState(buildStateKey('is-plan-not-found'), () => false)
const isPlanLoadError = useState(
  buildStateKey('is-plan-load-error'),
  () => false
)
const isLoading = useState(buildStateKey('is-loading'), () => false)
const planProfileId = useState<number | null | undefined>(
  buildStateKey('plan-profile-id'),
  () => undefined
)

const hasSubscriptionButtonText = computed(() => {
  return Boolean(props.formData.subscriptionButton?.text?.trim())
})

const hasActiveSubscription = computed(() => {
  return authStore.isProActive
})

const hiddenReasons = computed(() => {
  const reasons: string[] = []

  if (!props.formData.planId) {
    reasons.push('subscription plan is not selected')
  }

  if (!hasSubscriptionButtonText.value) {
    reasons.push('subscription button text is empty')
  }

  if (isPlanNotFound.value) {
    reasons.push('subscription plan was not found')
  }

  if (isPlanLoadError.value) {
    reasons.push('subscription plan data failed to load')
  }

  if (plan.value && !plan.value.is_visible) {
    reasons.push('subscription plan is hidden')
  }

  if (plan.value?.is_once_per_user && plan.value.was_used_by_user) {
    reasons.push('subscription plan can only be used once and was already used')
  }

  if (hasActiveSubscription.value) {
    reasons.push('user already has an active subscription')
  }

  return reasons
})

const isHiddenForCurrentUser = computed(() => {
  return hiddenReasons.value.length > 0
})

const shouldRenderBlock = computed(() => {
  if (!isResolved.value) {
    return false
  }

  return authStore.isAdmin || !isHiddenForCurrentUser.value
})

const adminHiddenMessage = computed(() => {
  if (!authStore.isAdmin || !isHiddenForCurrentUser.value) {
    return ''
  }

  return `This block is hidden for the current user, but it is visible because you are an admin.\nReason: ${hiddenReasons.value.join(
    '; '
  )}.`
})

const isAdditionalButtonsOnTheLeft = computed(() => {
  return props.formData.additionalButtonsPosition === 'left'
})

const isButtonsVisible = computed(() => {
  return (
    hasSubscriptionButtonText.value ||
    Boolean(props.formData.additionalButtons?.length)
  )
})

const isSubscriptionButtonDisabled = computed(() => {
  return (
    isPaymentDataLoading.value ||
    isLoading.value ||
    !plan.value ||
    isHiddenForCurrentUser.value
  )
})

const subscriptionButtonColor = computed(() => {
  return props.formData.subscriptionButton?.color || DEFAULT_COLOR_PRIMARY
})

const subscriptionButtonColorHover = computed(() => {
  return (
    props.formData.subscriptionButton?.colorHover || DEFAULT_COLOR_PRIMARY_HOVER
  )
})

const subscriptionButtonTextColor = computed(() => {
  return props.formData.subscriptionButton?.textColor || DEFAULT_COLOR_WHITE
})

const subscriptionButtonClass = computed(() => {
  const button = props.formData.subscriptionButton

  if (!button) {
    return ''
  }

  let classNames = 'editor-button '

  if (button.type === 'link') {
    classNames += 'editor-button_link'
  } else if (button.look === 'border') {
    classNames += 'editor-button_border'
  } else {
    classNames += 'editor-button_solid'
  }

  if (button.colorHover) {
    classNames += ' editor-button_hover-color'
  } else {
    classNames += ' editor-button_no-hover-color'
  }

  return classNames
})

const flexAlignClass = computed(() => {
  switch (props.formData.textAlign) {
    case 'left':
      return 'justify-start'
    case 'center':
      return 'justify-center'
    case 'right':
      return 'justify-end'
    default:
      return 'justify-center'
  }
})

const paddingClass = computed(() => {
  switch (props.formData.paddingSize) {
    case 'xs':
      return 'py-6 md:py-8'
    case 'sm':
      return 'py-14 md:py-20'
    case 'lg':
      return 'py-32 md:py-56'
    default:
      return 'py-20 md:py-40'
  }
})

async function load(): Promise<void> {
  const currentProfileId = authStore.profile?.id ?? null

  if (planProfileId.value === currentProfileId) {
    return
  }

  planProfileId.value = currentProfileId
  isLoading.value = true

  let nextPlan: Plan | null = null
  let nextPlanNotFound = false
  let nextPlanLoadError = false

  if (props.formData.planId) {
    try {
      nextPlan = await subscriptionsStore.getPlan(
        props.formData.planId,
        Boolean(authStore.profile),
        authStore.isAdmin
      )
    } catch (error: any) {
      if ((error as FetchError).status === 404) {
        nextPlanNotFound = true
      } else {
        nextPlanLoadError = true
        useLogError(error)
      }
    }
  }

  if (planProfileId.value !== currentProfileId) {
    return
  }

  plan.value = nextPlan
  isPlanNotFound.value = nextPlanNotFound
  isPlanLoadError.value = nextPlanLoadError
  isResolved.value = true
  isLoading.value = false
}

async function submit(): Promise<void> {
  if (isSubscriptionButtonDisabled.value) {
    return
  }

  await submitPlan(plan.value || undefined)
}

onServerPrefetch(async () => {
  await load()
})

onMounted(() => {
  load()
})

watch(
  () => authStore.profile,
  () => {
    load()
  }
)
</script>

<style lang="scss" scoped>
.editor-button {
  transition-duration: 150ms;

  &_border {
    color: v-bind(subscriptionButtonColor);
    border: 2px solid v-bind(subscriptionButtonColor);

    &.editor-button_hover-color:hover,
    &.editor-button_hover-color:focus {
      color: v-bind(subscriptionButtonColorHover);
      border-color: v-bind(subscriptionButtonColorHover);
    }
  }

  &_solid {
    background-color: v-bind(subscriptionButtonColor);
    color: v-bind(subscriptionButtonTextColor);

    &.editor-button_hover-color:hover,
    &.editor-button_hover-color:focus {
      background-color: v-bind(subscriptionButtonColorHover);
    }
  }

  &_link {
    color: v-bind(subscriptionButtonColor);

    &.editor-button_hover-color:hover,
    &.editor-button_hover-color:focus {
      color: v-bind(subscriptionButtonColorHover);
    }
  }

  &_no-hover-color:hover,
  &_no-hover-color:focus {
    filter: brightness(1.5);
  }
}
</style>
